import InfoIcon from '@mui/icons-material/Info';
import { Box, InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

const StringField = ({
  name,
  schema,
  value,
  onChange,
  onBlur,
  error,
  touched,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { title, description, pattern } = schema;
  const fieldId = `field-${name}`;
  const errorId = `${fieldId}-error`;

  const labelText = title || name;

  const handleChange = useCallback(
    (e) => {
      onChange(name, e.target.value);
    },
    [name, onChange]
  );

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);
      onBlur(name, e.target.value);
    },
    [name, onBlur]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const helperText = useMemo(() => {
    if (touched && error) return error;
    if (isFocused && pattern) return `Pattern: ${pattern}`;
    return description || '';
  }, [touched, error, isFocused, pattern, description]);

  const endAdornment = useMemo(() => {
    const parts = [];
    if (description) {
      parts.push(
        <Tooltip key="desc" title={description} arrow>
          <InfoIcon
            fontSize="small"
            sx={{ color: 'text.secondary', opacity: 0.75 }}
          />
        </Tooltip>
      );
    }
    if (pattern) {
      parts.push(
        <Tooltip key="pat" title={`Pattern: ${pattern}`}>
          <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Tooltip>
      );
    }
    if (parts.length === 0) return undefined;
    return <InputAdornment position="end">{parts}</InputAdornment>;
  }, [description, pattern]);

  return (
    <TextField
      fullWidth
      id={fieldId}
      name={name}
      label={labelText}
      value={value || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      error={touched && !!error}
      required={required}
      helperText={
        helperText ? (
          <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
            {helperText}
          </Box>
        ) : (
          ''
        )
      }
      aria-invalid={touched && !!error}
      aria-describedby={error ? errorId : undefined}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment,
      }}
    />
  );
};

StringField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    pattern: PropTypes.string,
  }).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.string,
  touched: PropTypes.bool,
  required: PropTypes.bool,
};

export default React.memo(StringField);
